import { navigate } from "../navigation";
import { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import type { LibraryExam } from "../../../../packages/published-content/library";
import { ToolDialog } from "../../../../packages/test-ui/tool-dialog";
import { displayTitle } from "../portal-data";
import { api } from "../api";
import { sectionScopeLabel, type SectionScope } from "../section-scope";
import { testSectionAvailability } from "./test-catalog-status";

export function StartDialog({ exam, onClose, initialSection = "full" }: { exam: LibraryExam; onClose: () => void; initialSection?: SectionScope }) {
  const [timing, setTiming] = useState(exam.eligibility === "adaptive_ready" ? "timed" : "untimed");
  const [section, setSection] = useState<SectionScope>(initialSection);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef<string | null>(null);
  const lock = useRef(false);

  async function start() {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError("");
    requestId.current ??= crypto.randomUUID();
    try {
      const result = await api<{ id: string }>("/api/attempts", {
        method: "POST",
        body: JSON.stringify({ examId: exam.id, timing, section, requestId: requestId.current })
      });
      navigate(`#attempt=${encodeURIComponent(result.id)}`);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      lock.current = false; setBusy(false);
    }
  }

  return (
    <ToolDialog title={displayTitle(exam.title)} className="portal-dialog" onClose={() => { if (!busy) onClose(); }}>
      <form className="portal-form" onSubmit={(event) => { event.preventDefault(); void start(); }}>
        <p className="field-note">{exam.eligibility === "adaptive_ready" && "Adaptive practice: Module 2 is selected automatically. Routing and scaled scores are estimates. "}Choose the practice that fits your time. Your previous results stay saved.</p>
        <fieldset className="practice-scope"><legend>What would you like to practice?</legend>
          {(["full", "rw", "math"] as const).map((scope) => {
            const availability = scope === "full" ? { available: exam.available, expected: exam.expected } : testSectionAvailability(exam, scope);
            const unavailable = availability?.available === 0;
            const detail = exam.eligibility === "adaptive_ready"
              ? `${scope === "full" ? 98 : scope === "rw" ? 54 : 44} questions · adaptive modules`
              : availability
              ? `${availability.available} of ${availability.expected} questions available${scope === "full" ? "" : " · two modules"}`
              : "Available questions in this section";
            return <label key={scope} className={section === scope ? "selected" : ""}>
              <input type="radio" name="practice-section" value={scope} checked={section === scope} disabled={busy || unavailable} onChange={() => { setSection(scope); requestId.current = null; }} />
              <span><strong>{sectionScopeLabel(scope)}</strong><small>{unavailable ? "No questions available" : detail}</small></span>
            </label>;
          })}
        </fieldset>
        <label>
          Timing
          <select value={timing} disabled={busy || exam.eligibility === "adaptive_ready"} onChange={(event) => { setTiming(event.target.value); requestId.current = null; }}>
            <option value="untimed">Untimed</option>
            {exam.supportsTiming && <option value="timed">Timed modules</option>}
          </select>
        </label>
        {timing === "timed" && (
          <p className="field-note">32 minutes per Reading and Writing module, 35 minutes per Math module. Save and exit pauses the clock.</p>
        )}
        <p className="field-note">If you already have this practice in progress, you’ll pick up where you left off.</p>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="secondary-button" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" disabled={busy}>
            {busy ? "Opening practice…" : "Begin practice"}
            <ArrowRight size={14} />
          </button>
        </div>
      </form>
    </ToolDialog>
  );
}
